import React from 'react';
import { Box, Typography, Stepper, Step, StepLabel, StepConnector, StepIconProps } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  LocalOffer as OfferIcon,
  ShoppingCart as OrderIcon,
  Payment as PaymentIcon,
  LocalShipping as DeliveryIcon,
  Inventory2 as PreparingIcon,
  FlightTakeoff as ShippedIcon,
  AirplaneTicket as InTransitIcon,
  DirectionsCar as OutForDeliveryIcon,
  Home as DeliveredIcon,
  ErrorOutline as FailedIcon,
  AssignmentReturn as ReturnedIcon,
  HourglassEmpty as PendingIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { PaidStatus, DeliveryStatus } from '../../services/purchasesService';
import { useTranslation } from '../../contexts/TranslationContext';
import { TransactionService } from '../../services/transactionService';

interface OrderTimelineProps {
  offerId?: string | null;
  paidStatus: PaidStatus;
  deliveryStatus?: DeliveryStatus;
  createdAt: string;
  completedAt?: string | null;
  trackingNumber?: string | null;
  orderId: string; // Purchase order ID for transaction lookup
  transactionId?: string | null;
  onPaymentClick?: () => void; // Callback when payment step is clicked
  onDeliveryClick?: () => void; // Callback when delivery step is clicked
}

interface OrderStep {
  label: string;
  icon: React.ReactNode;
  completed: boolean;
  active: boolean;
  error?: boolean;
  clickable?: boolean;
  warning?: boolean;
  next?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  '&.MuiStepConnector-root': {
    top: 22,
    left: 'calc(-50% + 20px)',
    right: 'calc(50% + 20px)',
  },
  '&.MuiStepConnector-line': {
    borderTopWidth: 3,
    borderRadius: 1,
  },
  '&.Mui-active .MuiStepConnector-line': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  '&.Mui-completed .MuiStepConnector-line': {
    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
  },
  '& .MuiStepConnector-line': {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const ColorlibStepIconRoot = styled('div')<{ ownerState: { completed?: boolean; active?: boolean; error?: boolean; warning?: boolean; clickable?: boolean; next?: boolean; disabled?: boolean } }>(
  ({ theme, ownerState }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
    zIndex: 1,
    color: '#fff',
    width: 44,
    height: 44,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    ...(ownerState.disabled && {
      background: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#e0e0e0',
      color: theme.palette.mode === 'dark' ? theme.palette.grey[500] : '#9e9e9e',
      boxShadow: 'none',
      animation: 'none',
      cursor: 'default',
      transform: 'none',
    }),
    ...(ownerState.active && {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
      animation: 'pulse 2s infinite',
      '@keyframes pulse': {
        '0%': {
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
        },
        '50%': {
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.6)',
        },
        '100%': {
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
        },
      },
    }),
    ...(ownerState.next && {
      background: 'linear-gradient(135deg, #00B0FF 0%, #0070BA 100%)',
      boxShadow: '0 6px 18px rgba(0, 176, 255, 0.55)',
      animation: 'nextPulse 1.6s infinite',
      '@keyframes nextPulse': {
        '0%': {
          boxShadow: '0 6px 18px rgba(0, 176, 255, 0.45)',
          transform: 'scale(1)',
        },
        '50%': {
          boxShadow: '0 10px 26px rgba(0, 176, 255, 0.75)',
          transform: 'scale(1.08)',
        },
        '100%': {
          boxShadow: '0 6px 18px rgba(0, 176, 255, 0.45)',
          transform: 'scale(1)',
        },
      },
    }),
    ...(ownerState.completed && !ownerState.warning && {
      background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
    }),
    ...(ownerState.error && {
      background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
      boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)',
    }),
    ...(ownerState.warning && {
      background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
      boxShadow: '0 4px 12px rgba(255, 152, 0, 0.4)',
      animation: 'warningPulse 2s infinite',
      '@keyframes warningPulse': {
        '0%': {
          boxShadow: '0 4px 12px rgba(255, 152, 0, 0.4)',
        },
        '50%': {
          boxShadow: '0 4px 20px rgba(255, 152, 0, 0.6)',
        },
        '100%': {
          boxShadow: '0 4px 12px rgba(255, 152, 0, 0.4)',
        },
      },
    }),
    ...(ownerState.clickable && !ownerState.warning && !ownerState.error && {
      cursor: 'pointer',
      '&:hover': {
        transform: 'scale(1.1)',
        boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
      },
    }),
  })
);

function ColorlibStepIcon(props: StepIconProps & { icon: React.ReactNode; error?: boolean; warning?: boolean; clickable?: boolean; next?: boolean; disabled?: boolean; onClick?: () => void }) {
  const { active, completed, icon, error, warning, clickable, next, disabled, onClick } = props;

  return (
    <ColorlibStepIconRoot 
      ownerState={{ completed, active, error, warning, clickable, next, disabled }}
      onClick={clickable && onClick ? onClick : undefined}
    >
      {disabled ? (
        <Box sx={{ color: 'inherit', opacity: 0.9, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {React.isValidElement(icon) ? React.cloneElement(icon as any, { sx: { ...(icon as any).props?.sx, fontSize: 24, color: 'inherit' } }) : icon}
        </Box>
      ) : (
        completed && !warning ? (
          <CheckCircleIcon sx={{ fontSize: 24, color: 'white' }} />
        ) : error ? (
          <FailedIcon sx={{ fontSize: 24, color: 'white' }} />
        ) : (
          <Box sx={{ color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </Box>
        )
      )}
    </ColorlibStepIconRoot>
  );
}

const OrderTimeline: React.FC<OrderTimelineProps> = ({
  offerId,
  paidStatus,
  deliveryStatus = 'pending',
  createdAt,
  completedAt,
  trackingNumber,
  orderId,
  transactionId,
  onPaymentClick,
  onDeliveryClick,
}) => {
  const { t } = useTranslation();
  const [hasTransaction, setHasTransaction] = React.useState<boolean | null>(null);
  const [checkingTransaction, setCheckingTransaction] = React.useState(false);

  // Check if transaction exists for this order
  React.useEffect(() => {
    const checkTransaction = async () => {
      if (!orderId) {
        setHasTransaction(false);
        return;
      }

      if (transactionId) {
        setHasTransaction(true);
        return;
      }

      setCheckingTransaction(true);
      try {
        const transactions = await TransactionService.getTransactionsByOrderId(orderId);
        setHasTransaction(transactions && transactions.length > 0);
      } catch (error) {
        console.error('Error checking transaction:', error);
        setHasTransaction(false);
      } finally {
        setCheckingTransaction(false);
      }
    };

    checkTransaction();
  }, [orderId, transactionId]);
  // Determine which steps are completed
  const showOfferStep = !!offerId;
  const offerCompleted = !!offerId;
  const orderCompleted = true; // Order is created (purchase exists)
  const paymentCompleted = paidStatus === 'succeeded';
  const transactionProblem = paymentCompleted && hasTransaction === false;
  
  // Determine delivery status and completion
  const deliveryCompleted = deliveryStatus === 'delivered';
  const deliveryInProgress = ['preparing', 'shipped', 'in_transit', 'out_for_delivery'].includes(deliveryStatus || 'pending');
  const deliveryFailed = deliveryStatus === 'failed' || deliveryStatus === 'returned';
  const deliveryNext = paymentCompleted && !transactionProblem && !deliveryCompleted && !deliveryFailed;

  // Get delivery icon based on status
  const getDeliveryIcon = () => {
    switch (deliveryStatus) {
      case 'preparing':
        return <PreparingIcon sx={{ fontSize: 24 }} />;
      case 'shipped':
        return <ShippedIcon sx={{ fontSize: 24 }} />;
      case 'in_transit':
        return <InTransitIcon sx={{ fontSize: 24 }} />;
      case 'out_for_delivery':
        return <OutForDeliveryIcon sx={{ fontSize: 24 }} />;
      case 'delivered':
        return <DeliveredIcon sx={{ fontSize: 24 }} />;
      case 'failed':
      case 'returned':
        return <FailedIcon sx={{ fontSize: 24 }} />;
      default:
        return <PendingIcon sx={{ fontSize: 24 }} />;
    }
  };

  // Get delivery label based on status
  const getDeliveryLabel = () => {
    switch (deliveryStatus) {
      case 'preparing':
        return t('dashboard.orders.timeline.preparing');
      case 'shipped':
        return t('dashboard.orders.timeline.shipped');
      case 'in_transit':
        return t('dashboard.orders.timeline.inTransit');
      case 'out_for_delivery':
        return t('dashboard.orders.timeline.outForDelivery');
      case 'delivered':
        return t('dashboard.orders.timeline.delivered');
      case 'failed':
        return t('dashboard.orders.timeline.deliveryFailed');
      case 'returned':
        return t('dashboard.orders.timeline.returned');
      default:
        return t('dashboard.orders.timeline.deliveryPending');
    }
  };

  // Determine active step key
  const activeKey: 'offer' | 'order' | 'payment' | 'delivery' | 'done' =
    showOfferStep && !offerCompleted
      ? 'offer'
      : !orderCompleted
      ? 'order'
      : !paymentCompleted
      ? 'payment'
      : !deliveryCompleted && !deliveryFailed
      ? 'delivery'
      : 'done';

  const steps: Array<OrderStep & { key: 'offer' | 'order' | 'payment' | 'delivery' }> = [];

  if (showOfferStep) {
    steps.push({
      key: 'offer',
      label: t('dashboard.orders.timeline.offerMade'),
      icon: <OfferIcon sx={{ fontSize: 24 }} />,
      completed: offerCompleted,
      active: activeKey === 'offer',
    });
  }

  steps.push(
    {
      key: 'order',
      label: t('dashboard.orders.timeline.orderCreated'),
      icon: <OrderIcon sx={{ fontSize: 24 }} />,
      completed: orderCompleted,
      active: activeKey === 'order',
    },
    {
      key: 'payment',
      label: t('dashboard.orders.timeline.payment'),
      icon: hasTransaction === false && paymentCompleted ? (
        <WarningIcon sx={{ fontSize: 24 }} />
      ) : (
        <PaymentIcon sx={{ fontSize: 24 }} />
      ),
      completed: paymentCompleted,
      active: activeKey === 'payment',
      clickable: onPaymentClick !== undefined,
      warning: hasTransaction === false && paymentCompleted,
      onClick: onPaymentClick,
    },
    {
      key: 'delivery',
      label: transactionProblem && !deliveryCompleted && !deliveryFailed
        ? 'Delivery will start after the transaction issue is resolved'
        : getDeliveryLabel(),
      icon: getDeliveryIcon(),
      completed: deliveryCompleted,
      active: activeKey === 'delivery',
      error: deliveryFailed,
      disabled: transactionProblem && !deliveryCompleted && !deliveryFailed,
      next: deliveryNext,
      clickable: !transactionProblem && paymentCompleted && onDeliveryClick !== undefined,
      onClick: !transactionProblem && paymentCompleted && onDeliveryClick ? onDeliveryClick : undefined,
    }
  );

  const activeStep = Math.max(0, steps.findIndex((s) => s.key === activeKey));

  return (
    <Box
      sx={{
        width: '100%',
        py: 2,
        px: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        borderRadius: 2,
        border: '1px solid rgba(0, 0, 0, 0.05)',
      }}
    >
      {trackingNumber && (
        <Box
          sx={{
            mb: 1.5,
            px: 2,
            py: 1,
            borderRadius: 1,
            backgroundColor: 'rgba(0, 112, 186, 0.05)',
            border: '1px solid rgba(0, 112, 186, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <DeliveryIcon sx={{ fontSize: 18, color: '#0070BA' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#0070BA' }}>
            {t('dashboard.orders.tracking.label')} {trackingNumber}
          </Typography>
        </Box>
      )}
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        connector={<ColorlibConnector />}
        sx={{
          '& .MuiStepLabel-root': {
            '& .MuiStepLabel-label': {
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'text.secondary',
              '&.Mui-active': {
                color: '#667eea',
                fontWeight: 700,
              },
              '&.Mui-completed': {
                color: '#4CAF50',
                fontWeight: 700,
              },
            },
          },
        }}
      >
        {steps.map((step, index) => (
          <Step key={step.label} completed={step.completed} active={step.active}>
            <StepLabel
              StepIconComponent={(props) => (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                >
                  <ColorlibStepIcon
                    {...props}
                    error={step.error}
                    warning={step.warning}
                    clickable={step.clickable}
                    disabled={step.disabled}
                    next={step.next}
                    onClick={step.onClick}
                    icon={
                      step.completed && !step.warning ? (
                        <CheckCircleIcon sx={{ fontSize: 24 }} />
                      ) : step.error ? (
                        <FailedIcon sx={{ fontSize: 24 }} />
                      ) : (
                        step.icon
                      )
                    }
                  />
                </motion.div>
              )}
            >
              <Typography
                variant="caption"
                onClick={step.clickable && step.onClick ? step.onClick : undefined}
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: step.completed || step.active ? 600 : 500,
                  color:
                    step.error
                      ? '#f44336'
                      : step.warning
                      ? '#ff9800'
                      : step.disabled
                      ? 'text.disabled'
                      : step.next
                      ? '#0070BA'
                      : step.completed || step.active
                      ? step.completed
                        ? '#4CAF50'
                        : '#667eea'
                      : 'text.secondary',
                  cursor: step.clickable ? 'pointer' : 'default',
                  textDecoration: step.clickable ? 'underline' : 'none',
                  '&:hover': step.clickable ? {
                    opacity: 0.8,
                  } : {},
                }}
              >
                {step.label}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default OrderTimeline;

